import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { SUPPORT_EMAIL, COMPANY_NAME, WEBSITE_URL } from '../constants/contact'

export default function Terms() {
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-paybyphone-light">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="text-xl font-bold text-paybyphone-green hover:text-paybyphone-green/80 transition-colors">
            goparkingnow
          </Link>
          {user && (
            <Link to="/dashboard" className="text-paybyphone-green hover:text-paybyphone-green/80">
              ← Back to Dashboard
            </Link>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms and Conditions / Conditions Générales de Vente</h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Effective date:</strong> January 1, 2025<br />
              <strong>Last updated:</strong> March 5, 2026<br />
              <strong>Date d'entrée en vigueur :</strong> 1er janvier 2025<br />
              <strong>Dernière mise à jour :</strong> 5 mars 2026
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">English version</h2>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. About the Service</h2>
              <p className="text-gray-700 mb-4">
                Welcome to goparkingnow.com (the “Service”), operated by {COMPANY_NAME} (“Company”, “we”, “us”, or “our”).
                The Service allows users to locate parking areas, save the position of their vehicle, use parking-related features,
                and access premium or pay-per-use functionalities depending on the selected plan.
              </p>
              <p className="text-gray-700">
                By accessing or using the Service, you agree to be bound by these Terms and Conditions. If you do not agree to
                these Terms, you must not use the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Pricing Plans and Billing</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Monthly subscription</h3>
              <p className="text-gray-700 mb-4">
                The Service may be offered under a monthly subscription plan. When you choose the monthly plan, you are billed the
                amount displayed at checkout for one month of access to the Service. Unless otherwise stated at checkout, the monthly
                subscription renews automatically at the end of each billing period until cancelled.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Weekly subscription</h3>
              <p className="text-gray-700 mb-4">
                The Service may also be offered under a weekly subscription plan. When you choose the weekly plan, you are billed the
                amount displayed at checkout for one week of access to the Service. Unless otherwise stated at checkout, the weekly
                subscription renews automatically every week until cancelled.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Standard subscription / normal subscription</h3>
              <p className="text-gray-700 mb-4">
                Any “normal subscription”, “standard subscription”, or similar wording used on the Service refers to the subscription
                offer presented to the user at checkout, which may be monthly or weekly depending on the sales page or payment path used.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.4 Pay As You Go</h3>
              <p className="text-gray-700 mb-4">
                The Service may also offer a “Pay As You Go” formula, with pricing calculated according to the duration selected by the
                user and billed immediately as indicated at checkout.
              </p>
              <p className="text-gray-700 mb-4">
                IMPORTANT: when the Pay As You Go offer is presented through the specific SubDirect flow with a 24-hour trial/activation
                mechanism, the user expressly agrees that, after the selected parking duration and the 24-hour trial period, a monthly
                subscription of $49.99 per month will start automatically unless cancelled before the end of the trial period.
              </p>
              <p className="text-gray-700 mb-4 font-bold">
                IMPORTANT: THE PAY AS YOU GO PRICE DOES NOT, UNDER ANY CIRCUMSTANCES, CORRESPOND TO THE PAYMENT OF THE PHYSICAL PARKING
                SPACE OR OF ANY PUBLIC OR PRIVATE PARKING FEE. IT ONLY CORRESPONDS TO THE COST OF USING THE APPLICATION, INCLUDING THE
                HOURLY SAVING OF THE VEHICLE POSITION ON THE USER'S PHONE AND IN OUR DATABASES.
              </p>
              <p className="text-gray-700 mb-4">
                The user remains solely responsible for paying any parking meter, public parking, private parking, road usage, fines,
                or other third-party parking charges that may apply in the real world.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.5 Trial / activation flows</h3>
              <p className="text-gray-700 mb-4">
                Certain sales flows may include an activation, card registration, free trial, or temporary trial offer before a paid
                subscription starts. In such cases, the exact amount charged, if any, the duration of the trial, and the post-trial
                billing conditions are those expressly shown to the user before payment validation.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.6 Payment methods and processors</h3>
              <p className="text-gray-700 mb-4">
                Payments may be processed through SystemPay and/or Stripe, depending on the subscription path, country, configuration,
                or technical routing used at the time of purchase. By submitting your payment information, you authorize the relevant
                payment processor to charge the selected plan or usage amount.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.7 Auto-renewal and immediate billing</h3>
              <p className="text-gray-700 mb-4">
                Weekly and monthly subscriptions renew automatically unless cancelled before the next billing date. Pay As You Go usage
                is billed immediately based on the selected duration and does not constitute payment for a parking spot itself.
              </p>
              <p className="text-gray-700 mb-4">
                For avoidance of doubt, when a user selects the Pay As You Go formula through the SubDirect path that includes a trial,
                the user also authorizes the automatic start of a monthly subscription at $49.99 per month after the 24-hour trial,
                unless the subscription is cancelled in time.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Cancellation Policy</h2>
              <p className="text-gray-700 mb-4">
                You may cancel your subscription at any time from your account area when this option is available, or by contacting support.
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>For weekly or monthly subscriptions, cancellation stops future renewals from the next billing cycle.</li>
                <li>Access may remain active until the end of the already-paid period, unless otherwise stated in the offer.</li>
                <li>Amounts already charged for an active billing period are non-refundable, except where mandatory law provides otherwise.</li>
                <li>For Pay As You Go usage, the billed amount corresponds to the application usage already initiated or consumed and is generally non-refundable.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Right of Withdrawal</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 General principle</h3>
              <p className="text-gray-700 mb-4">
                If consumer protection laws apply to your purchase, you may benefit from a legal withdrawal period, except in cases where
                an exception applies under applicable law.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Digital services exception</h3>
              <p className="text-gray-700 mb-4">
                Where the Service consists of digital content or digital services supplied immediately after purchase, you expressly request
                immediate performance and acknowledge that your withdrawal right may be lost or limited once the Service has begun, to the
                extent permitted by applicable law.
              </p>
              <p className="text-gray-700 mb-4">
                By validating your purchase and using the Service, you expressly consent to the immediate supply of the digital service.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  <strong>For customers located in France or the European Union, legal rights remain subject to mandatory consumer law.</strong>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. User Responsibilities</h2>
              <p className="text-gray-700 mb-4">By using the Service, you agree to:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Provide accurate and complete information when creating your account and making a payment.</li>
                <li>Keep your login credentials and payment access secure.</li>
                <li>Use the Service only for lawful purposes.</li>
                <li>Understand that the Service helps you locate or save parking-related information, but does not replace your obligation to comply with local parking rules.</li>
                <li>Pay all real-world parking fees, tickets, tolls, or penalties due to third parties.</li>
                <li>Not interfere with, damage, or attempt unauthorized access to the Service.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Service Availability</h2>
              <p className="text-gray-700 mb-4">
                We aim to provide continuous access to the Service, but availability is not guaranteed at all times. The Service may be
                temporarily unavailable because of maintenance, updates, technical incidents, payment provider issues, connectivity issues,
                or circumstances beyond our control.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, {COMPANY_NAME} shall not be liable for indirect, incidental, special,
                consequential, or punitive damages, including loss of profits, data, use, goodwill, or other intangible losses arising
                out of or related to your use of the Service. We are not responsible for parking availability, parking enforcement,
                parking prices charged by third parties, or any fines or penalties issued by public or private authorities.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Changes to the Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these Terms at any time. Any material changes may be notified by publication on this page
                and by updating the “Last updated” date. Continued use of the Service after such changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions regarding these Terms and Conditions, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>{COMPANY_NAME}</strong><br />
                  Website: {WEBSITE_URL}<br />
                  Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green hover:text-green-600 font-medium">{SUPPORT_EMAIL}</a>
                </p>
              </div>
            </section>

            <hr className="my-10" />

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Version française</h2>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. À propos du service</h2>
              <p className="text-gray-700 mb-4">
                Bienvenue sur goparkingnow.com (le « Service »), exploité par {COMPANY_NAME} (« Société », « nous », « notre » ou « nos »).
                Le Service permet notamment aux utilisateurs de localiser des zones de stationnement, de sauvegarder la position de leur
                véhicule, d'utiliser des fonctionnalités liées au parking et d'accéder à des offres premium ou à l'usage selon la formule choisie.
              </p>
              <p className="text-gray-700">
                En accédant au Service ou en l'utilisant, vous acceptez d'être lié par les présentes Conditions Générales de Vente et
                d'Utilisation. Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser le Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Offres, tarification et facturation</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Abonnement mensuel</h3>
              <p className="text-gray-700 mb-4">
                Le Service peut être proposé sous la forme d'un abonnement mensuel. Lorsque vous choisissez cette formule, le montant affiché
                au moment du paiement est facturé pour un mois d'accès au Service. Sauf indication contraire au moment du paiement,
                l'abonnement mensuel est reconduit automatiquement à chaque échéance jusqu'à annulation.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Abonnement hebdomadaire</h3>
              <p className="text-gray-700 mb-4">
                Le Service peut également être proposé sous la forme d'un abonnement hebdomadaire. Lorsque vous choisissez cette formule,
                le montant affiché au moment du paiement est facturé pour une semaine d'accès au Service. Sauf indication contraire,
                l'abonnement hebdomadaire est reconduit automatiquement chaque semaine jusqu'à annulation.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Abonnement normal / standard</h3>
              <p className="text-gray-700 mb-4">
                Toute mention d'« abonnement normal », « abonnement standard » ou formulation équivalente sur le Service désigne l'offre
                d'abonnement présentée à l'utilisateur lors du paiement, laquelle peut être mensuelle ou hebdomadaire selon la page de vente
                ou le parcours de paiement utilisé.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.4 Pay As You Go</h3>
              <p className="text-gray-700 mb-4">
                Le Service peut aussi proposer une formule « Pay As You Go », dont le prix est calculé selon la durée choisie par
                l'utilisateur et débité immédiatement selon les indications affichées au moment du paiement.
              </p>
              <p className="text-gray-700 mb-4">
                IMPORTANT : lorsque l'offre Pay As You Go est proposée via le parcours spécifique SubDirect avec un mécanisme d'essai ou
                d'activation de 24 heures, l'utilisateur accepte expressément qu'à l'issue de la durée de stationnement choisie et de
                la période d'essai de 24 heures, un abonnement mensuel de 49,99 € par mois débute automatiquement, sauf annulation avant
                la fin de la période d'essai.
              </p>
              <p className="text-gray-700 mb-4 font-bold">
                IMPORTANT : LE PAY AS YOU GO NE CORRESPOND EN AUCUN CAS AU PAIEMENT DU STATIONNEMENT LUI-MÊME, NI À UN DROIT DE PLACE,
                NI À UN HORODATEUR, NI À DES FRAIS DE PARKING PUBLIC OU PRIVÉ. IL CORRESPOND UNIQUEMENT AU COÛT D'UTILISATION DE
                L'APPLICATION, NOTAMMENT POUR LA SAUVEGARDE DE LA POSITION DU VÉHICULE PAR HEURE, DANS LE TÉLÉPHONE DE
                L'UTILISATEUR ET DANS NOS BASES DE DONNÉES.
              </p>
              <p className="text-gray-700 mb-4">
                L'utilisateur reste seul responsable du paiement de tout stationnement réel, horodateur, parking public, parking privé,
                amende, redevance ou frais dus à des tiers dans le monde réel.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.5 Parcours d'essai ou d'activation</h3>
              <p className="text-gray-700 mb-4">
                Certains parcours de vente peuvent prévoir une activation, un enregistrement de carte, un essai gratuit ou une offre d'essai
                temporaire avant le démarrage d'un abonnement payant. Dans ce cas, le montant éventuellement débité, la durée de l'essai
                et les conditions de facturation après essai sont celles expressément affichées à l'utilisateur avant la validation du paiement.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.6 Moyens de paiement et prestataires</h3>
              <p className="text-gray-700 mb-4">
                Les paiements peuvent être traités par SystemPay et/ou Stripe, selon le parcours de souscription, le pays, la configuration
                technique ou le routage utilisé au moment de l'achat. En renseignant vos informations de paiement, vous autorisez le
                prestataire de paiement concerné à débiter le montant de l'offre ou de l'usage sélectionné.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.7 Reconduction automatique et débit immédiat</h3>
              <p className="text-gray-700 mb-4">
                Les abonnements hebdomadaires et mensuels se renouvellent automatiquement sauf annulation avant la prochaine date de facturation.
                Le Pay As You Go est débité immédiatement en fonction de la durée choisie et ne constitue jamais le paiement d'une place de parking elle-même.
              </p>
              <p className="text-gray-700 mb-4">
                Afin d'éviter toute ambiguïté, lorsqu'un utilisateur choisit la formule Pay As You Go via le parcours SubDirect incluant
                un essai, il autorise également le démarrage automatique d'un abonnement mensuel à 49,99 € par mois après les 24 heures
                d'essai, sauf annulation effectuée à temps.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Politique d'annulation</h2>
              <p className="text-gray-700 mb-4">
                Vous pouvez annuler votre abonnement à tout moment depuis votre espace compte lorsque cette option est disponible, ou en
                contactant le support.
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Pour les abonnements hebdomadaires ou mensuels, l'annulation met fin aux renouvellements futurs à compter du prochain cycle.</li>
                <li>L'accès au Service peut rester actif jusqu'à la fin de la période déjà payée, sauf indication contraire dans l'offre.</li>
                <li>Les montants déjà facturés pour une période en cours ne sont pas remboursables, sauf disposition légale impérative contraire.</li>
                <li>Pour le Pay As You Go, le montant facturé correspond à un usage de l'application déjà initié ou consommé et n'est en principe pas remboursable.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Droit de rétractation</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Principe général</h3>
              <p className="text-gray-700 mb-4">
                Si les règles de protection du consommateur s'appliquent à votre achat, vous pouvez bénéficier d'un droit légal de
                rétractation, sauf lorsqu'une exception prévue par la loi s'applique.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Exception liée aux services numériques</h3>
              <p className="text-gray-700 mb-4">
                Lorsque le Service consiste en un contenu numérique ou un service numérique fourni immédiatement après l'achat, vous demandez
                expressément son exécution immédiate et reconnaissez que votre droit de rétractation peut être perdu ou limité dès le début
                d'exécution du Service, dans les limites permises par la loi applicable.
              </p>
              <p className="text-gray-700 mb-4">
                En validant votre achat et en utilisant le Service, vous consentez expressément à la fourniture immédiate du service numérique.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  <strong>Pour les clients situés en France ou dans l'Union européenne, les droits légaux restent soumis aux dispositions impératives du droit de la consommation.</strong>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Responsabilités de l'utilisateur</h2>
              <p className="text-gray-700 mb-4">En utilisant le Service, vous vous engagez à :</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Fournir des informations exactes et complètes lors de la création de votre compte et lors du paiement.</li>
                <li>Préserver la sécurité de vos identifiants et de vos accès de paiement.</li>
                <li>Utiliser le Service uniquement à des fins légales.</li>
                <li>Comprendre que le Service vous aide à localiser ou sauvegarder des informations liées au stationnement, sans vous dispenser du respect des règles locales de stationnement.</li>
                <li>Payer tous les frais réels de stationnement, horodateurs, contraventions, péages ou pénalités dus à des tiers.</li>
                <li>Ne pas perturber, endommager ou tenter d'accéder sans autorisation au Service.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Disponibilité du service</h2>
              <p className="text-gray-700 mb-4">
                Nous faisons nos meilleurs efforts pour assurer l'accès continu au Service, sans toutefois garantir sa disponibilité permanente.
                Le Service peut être temporairement indisponible en raison d'opérations de maintenance, de mises à jour, d'incidents techniques,
                de problèmes liés aux prestataires de paiement, de connectivité ou de circonstances indépendantes de notre volonté.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Limitation de responsabilité</h2>
              <p className="text-gray-700 mb-4">
                Dans la mesure maximale permise par la loi, {COMPANY_NAME} ne pourra être tenue responsable des dommages indirects,
                accessoires, spéciaux, consécutifs ou punitifs, y compris les pertes de profits, de données, d'usage, de clientèle ou
                d'autres pertes immatérielles résultant de l'utilisation du Service. Nous ne sommes pas responsables de la disponibilité
                réelle des stationnements, du contrôle du stationnement, des tarifs de parking pratiqués par des tiers, ni des amendes ou
                pénalités émises par des autorités publiques ou privées.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Modification des conditions</h2>
              <p className="text-gray-700 mb-4">
                Nous nous réservons le droit de modifier les présentes conditions à tout moment. Toute modification substantielle peut être
                portée à votre connaissance par publication sur cette page et par mise à jour de la date de « Dernière mise à jour ».
                La poursuite de l'utilisation du Service après modification vaut acceptation des conditions mises à jour.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Informations de contact</h2>
              <p className="text-gray-700 mb-4">
                Si vous avez des questions concernant les présentes Conditions Générales de Vente et d'Utilisation, vous pouvez nous contacter :
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>{COMPANY_NAME}</strong><br />
                  Website / Site web: {WEBSITE_URL}<br />
                  Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green hover:text-green-600 font-medium">{SUPPORT_EMAIL}</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
